import React, { useEffect, useRef, useState, useCallback } from "react";

interface SpotlightBackgroundProps {
  className?: string;
  /** Radius of the spotlight in pixels */
  radius?: number;
  /** Smoothing factor (0-1, lower = smoother/slower) */
  smoothing?: number;
  /** Content to reveal under the spotlight (typically an image) */
  children?: React.ReactNode;
}

const SpotlightBackground: React.FC<SpotlightBackgroundProps> = ({
  className = "",
  radius = 400,
  smoothing = 0.08,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: 0.5, y: 0.5 });
  const targetRef = useRef({ x: 0.5, y: 0.5 });
  const rafRef = useRef<number>();
  const hasMouseRef = useRef(false);
  const ambientRef = useRef(0);
  const [maskStyle, setMaskStyle] = useState("");

  const updateMask = useCallback(() => {
    posRef.current.x += (targetRef.current.x - posRef.current.x) * smoothing;
    posRef.current.y += (targetRef.current.y - posRef.current.y) * smoothing;

    // Ambient drift when no mouse
    if (!hasMouseRef.current) {
      ambientRef.current += 0.008;
      const t = ambientRef.current;
      targetRef.current.x = 0.5 + Math.sin(t * 0.9) * 0.48 + Math.sin(t * 0.37) * 0.1;
      targetRef.current.y = 0.5 + Math.sin(t * 0.43 + 2.0) * 0.48 + Math.cos(t * 0.71) * 0.08;
    }

    const x = posRef.current.x * 100;
    const y = posRef.current.y * 100;

    setMaskStyle(
      `radial-gradient(${radius}px circle at ${x}% ${y}%, black 0%, transparent 70%)`
    );

    rafRef.current = requestAnimationFrame(updateMask);
  }, [radius, smoothing]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(updateMask);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateMask]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      // Only track if mouse is roughly over the container area
      if (x >= -0.1 && x <= 1.1 && y >= -0.1 && y <= 1.1) {
        hasMouseRef.current = true;
        targetRef.current.x = Math.max(0, Math.min(1, x));
        targetRef.current.y = Math.max(0, Math.min(1, y));
      } else {
        hasMouseRef.current = false;
      }
    };

    const handleMouseLeave = () => {
      hasMouseRef.current = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`pointer-events-auto ${className}`}
      style={{
        maskImage: maskStyle,
        WebkitMaskImage: maskStyle,
      }}
    >
      {children}
    </div>
  );
};

export default SpotlightBackground;
